import { toReplayEventIconFeatures } from '@components/map/layers/event/event.mapper';
import { selectActualMapFilters } from '@redux/settings/settings.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { createSelector } from '@reduxjs/toolkit';
import { isCameraActiveMonitoringSensor } from '@utils/sensors/camera/camera.utils';
import { isActiveMonitoringSensor } from '@utils/sensors/sensors.utils';
import { add, isWithinInterval } from 'date-fns';
import { FeatureCollection } from 'geojson';

import { MapFilters } from '@/types/filters.types';
import { ReplayEvent, ReplayStorageConfiguration } from '@/types/replay/replay.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';

import { RootState } from '../store';
import { replayEventsAdapter, replayManualPeriodsAdapter } from './replay.adapters';
import { ReplayState } from './replay.reducer';

function selectReplayState(state: RootState): ReplayState {
  return state.replay;
}

const { selectAll: selectAllReplayEvents } = replayEventsAdapter.getSelectors(
  (state: RootState) => selectReplayState(state).replayEvents,
);

export const ReplayEventSelectors = {
  selectAllReplayEvents,
};

const { selectAll: selectAllReplaySituationEvents } = replayEventsAdapter.getSelectors(
  (state: RootState) => selectReplayState(state).replaySituationEvents,
);

const { selectAll: selectAllReplayManualPeriods } = replayManualPeriodsAdapter.getSelectors(
  (state: RootState) => selectReplayState(state).replayManualPeriods,
);

export const ReplayManualPeriodSelectors = {
  selectAllReplayManualPeriods,
};

export const selectReplayStorageConfiguration = createSelector(
  selectReplayState,
  (state: ReplayState): ReplayStorageConfiguration | null => state.replayStorageConfiguration,
);

const selectEventsByReplayMode = createSelector(
  [selectAllReplayEvents, selectAllReplaySituationEvents, (state, replayMode = false) => replayMode],
  (replayEvents, replaySituationEvents, replayMode): ReplayEvent[] => {
    if (replayMode) {
      return replaySituationEvents;
    }
    return replayEvents;
  },
);

export const selectOngoingReplayEvent = createSelector(
  [selectEventsByReplayMode, selectSituationTime],
  (events: ReplayEvent[], time: string | null): ReplayEvent[] =>
    events.filter(
      (event) =>
        time &&
        isWithinInterval(new Date(time), {
          start: new Date(event.startDate),
          end: add(new Date(event.startDate), { minutes: event.duration }),
        }),
    ),
);

export const selectFilteredReplayEvents = createSelector(
  [selectOngoingReplayEvent, selectActualMapFilters],
  (events: ReplayEvent[], mapFilters: MapFilters): ReplayEvent[] => (mapFilters.displayOtherInfoEvent ? events : []),
);

export const selectReplayEventFeatures = createSelector(
  selectFilteredReplayEvents,
  (events: ReplayEvent[]): FeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: toReplayEventIconFeatures(events),
    };
  },
);

export const selectReplayStatus = createSelector(selectReplayState, (state) => state.status);

export const selectReplaySpeed = createSelector(selectReplayStatus, (status) => status?.speed);

export const selectReplayUuid = createSelector(selectReplayStatus, (status) => status?.uuid);

export const selectReplayStatusTime = createSelector(selectReplayStatus, (status) =>
  status?.time ? new Date(status.time) : undefined,
);

export const selectReplaySettings = createSelector(selectReplayState, (state) => state.replaySettings);

export const selectReplayStartTime = createSelector(selectReplayState, (state) => state.replayStartTime);

export const selectReplayStartPosition = createSelector(selectReplayState, (state) => state.replayStartPosition);

export const selectReplaySituation = createSelector(selectReplayState, (state) => state.situation);

export const selectReplayActiveMonitoringSensors = createSelector(
  selectReplaySituation,
  // Prevent a npe for replay older than 5.7.0.0
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  (state) => state?.tacticalSituation?.monitoring.monitoringSensors.sensors?.filter(isActiveMonitoringSensor) ?? [],
);

export const selectReplayActiveCameraConfigurations = createSelector(
  selectReplayActiveMonitoringSensors,
  (state): CameraConfiguration[] => {
    return state.filter(isCameraActiveMonitoringSensor).map((status) => status.configuration);
  },
);
